import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { Autocomplete, TextField, Button } from "@mui/material";
import { esES } from "@mui/x-data-grid/locales";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getInvoices, showInvoice } from "../../controllers/api_invoices";
import { createDispatchGuide, showDispatchGuide } from "../../controllers/apiDispatchGuide";
import { showProfileClient } from "../../controllers/apiProfile";
import { decryptText } from "../../controllers/api";
import { preventEnterSubmit, FORM_FOOTER_CONTAINER, FORM_BTN_CANCEL, FORM_BTN_SUBMIT } from "../../utils/formHelpers";
// (2026-07-08) Cliente axios autenticado del proyecto (Authorization: Bearer
// <token> vía interceptor) — usado para cargar /api/config/tipos-licencia.
import api from "../../services/axiosConfig";

// (2026-07-03) SENIAT Fase 4 (Guía tipo 04): campos nuevos que acepta el
// POST /dispatch-guides. Regla transversal GGTIC.GIT.00.02 v1.4: NO se
// emiten etiquetas vacías, por lo que estos campos se OMITEN del payload
// cuando vienen vacíos (ver handleSubmit).
const CAMPOS_SENIAT_GUIA = [
  "motivo_traslado_codigo",
  "descripcion_servicio",
  // (2026-07-14 Fase C) Los Catálogos 14/15/16 NO tienen código normativo, así
  // que el backend EXIGE el ÍNDICE (`..._id`) y ya no acepta el string.
  "tipo_producto_id",
  "origen_producto_id",
  "destino_producto_id",
  "conductor_identificacion",
  "conductor_tipo_licencia",
  "conductor_contacto",
  "vehiculo_tipo",
  "vehiculo_numero",
  "transportista_rif",
  "transportista_domicilio",
];

// ******************************************************************
// FORM PRINCIPAL
// ******************************************************************
function FormDispatchGuides() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dispatchGuide_id = queryParams.get("id");

  const apiRef = useGridApiRef();

  const [DispatchGuides, setDispatchGuides] = useState([]);
  const [filterResults, setFilterResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [searchControl, setSearchControl] = useState("");
  const [errors, setErrors] = useState({}); // Estado para errores de validación
  // (2026-07-08) SENIAT Fase 4: opciones del desplegable "Tipo de licencia"
  // (Catálogo config /api/config/tipos-licencia) — 5 grados de licencia VE.
  const [tiposLicencia, setTiposLicencia] = useState([]);
  // (2026-07-14 Fase C) Catálogos de la guía LEÍDOS DE LA BD (Cat 13/14/15/16).
  // Nada de listas fijadas en el frontend: si SENIAT agrega un valor, aparece
  // solo. Los Cat 14/15/16 se envían por `id`; el Cat 13 por `codigo`.
  const [catalogosGuia, setCatalogosGuia] = useState({
    motivo_traslado: [],
    tipo_producto: [],
    origen_producto: [],
    destino_producto: [],
  });
  const authData = JSON.parse(localStorage.getItem("authData") || "{}");
  const authclientId = authData.empresa_id;
  const today = new Date().toISOString().split("T")[0];

  const [dispatchGuide, setDispatchGuide] = useState({
    id: null,
    "factura_id": 0,
    "fecha_salida": today,
    "factura_cliente_final_rif": '',
    "factura_cliente_final_nombre": '',
    "factura_numero_factura": '',
    "factura_numero_control": '',
    "tercero_id": null,
    "origen": "Almacén Principal",
    "destino": "",
    "motivo_traslado": "",
    "transportista": "",
    "chofer": "",
    "placa": "",
    // (2026-07-03) SENIAT Fase 4 (Guía tipo 04): motivo por Catálogo 13; el
    // texto libre motivo_traslado pasa a ser la especificación del motivo.
    // Qué exige cada motivo lo dicen los FLAGS del catálogo, no el código.
    "motivo_traslado_codigo": "",
    "descripcion_servicio": "",
    // (2026-07-14 Fase C) Catálogos 14/15/16: se envía el ÍNDICE del catálogo
    // (no tienen código normativo). Vacío = N/A, no se envía.
    "tipo_producto_id": "",
    "origen_producto_id": "",
    "destino_producto_id": "",
    // Conductor / Vehículo / Transportista (tabla 28 del doc SENIAT).
    "conductor_identificacion": "",
    "conductor_tipo_licencia": "",
    "conductor_contacto": "",
    "vehiculo_tipo": "",
    "vehiculo_numero": "",
    "transportista_rif": "",
    "transportista_domicilio": "",
  });


  // **********************************************************
  // CARGAR DATOS CLIENTE
  // **********************************************************
  useEffect(() => {
    if (!authclientId) return;

    showProfileClient(authclientId)
      .then((client) => {
        setDispatchGuide((prev) => ({
          ...prev,
          sujeto_retenido_rif: client.rif || "",
          sujeto_retenido_nombre: client.nombre_empresa || "",
        }));
      })
      .catch((err) => console.error(err));
  }, [authclientId]);

  // (2026-07-14 Fase C) Fila del Catálogo 13 seleccionada. Sus FLAGS gobiernan
  // qué campos son obligatorios/visibles — nada de comparar contra 'X1'/'X3'.
  const motivoSel = catalogosGuia.motivo_traslado.find(
    (m) => m.codigo === dispatchGuide.motivo_traslado_codigo
  );

  // **********************************************************
  // CARGAR TIPOS DE LICENCIA (Catálogo config) — una sola vez
  // **********************************************************
  useEffect(() => {
    // Mismo cliente axios autenticado que usan los servicios del form
    // (Authorization: Bearer <token> vía interceptor de axiosConfig).
    api
      .get("/api/config/tipos-licencia")
      .then((res) => {
        setTiposLicencia(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => console.error("Error cargando tipos de licencia:", err));
  }, []);

  // **********************************************************
  // CARGAR CATÁLOGOS DE LA GUÍA (Cat 13/14/15/16) — una sola vez
  // (2026-07-14 Fase C) Vienen de la BD, no de listas en el código.
  // **********************************************************
  useEffect(() => {
    api
      .get("/api/config/catalogos-guia")
      .then((res) => {
        const d = res.data || {};
        setCatalogosGuia({
          motivo_traslado: Array.isArray(d.motivo_traslado) ? d.motivo_traslado : [],
          tipo_producto: Array.isArray(d.tipo_producto) ? d.tipo_producto : [],
          origen_producto: Array.isArray(d.origen_producto) ? d.origen_producto : [],
          destino_producto: Array.isArray(d.destino_producto) ? d.destino_producto : [],
        });
      })
      .catch((err) => console.error("Error cargando catálogos de la guía:", err));
  }, []);

  // **********************************************************
  // CARGAR RETENCIONES + RETENCIÓN EXISTENTE
  // **********************************************************
  useEffect(() => {
    const fetchData = async () => {
      try {
        //const data = await getDispatchGuides();
        //setDispatchGuides(data);
        console.log('dispatchGuide_id: ', dispatchGuide_id);
        if (dispatchGuide_id) {
          const dataDispatchGuide = await showDispatchGuide(
            decryptText(dispatchGuide_id)
          );
          setDispatchGuide(dataDispatchGuide);
        }
      } catch {
        toast.error("Error cargando datos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatchGuide_id]);

  // **********************************************************
  // FECHA EMISIÓN
  // **********************************************************
  const handleFechaEmisionChange = (value) => {
    const periodo = value.slice(0, 7);
    setDispatchGuide((prev) => ({
      ...prev,
      fecha_emision: value,
      periodo_fiscal: periodo,
    }));
  };
  const handleFechaEntregaChange = (value) => {
    setDispatchGuide((prev) => ({
      ...prev,
      fecha_salida: value,
    }));
  };

  // **********************************************************
  // BUSCAR FACTURAS
  // **********************************************************
  const handleSearchChange = async (value) => {
    setSearchControl(value);
    if (value.length < 3) return;

    try {
      const response = await getInvoices({
        tipo_documento: "FC",
        numero_control: value,
        estatus: 'normal',
      });
      setFilterResults(Array.isArray(response.data) ? response.data : []);
    } catch {
      setFilterResults([]);
    }
  };

  // **********************************************************
  // SELECCIONAR FACTURA
  // **********************************************************
  const handleInvoiceSelect = async (invoice) => {
    if (!invoice?.id) return;

    try {
      const fullInvoice = await showInvoice(invoice.id);
      const inv = fullInvoice || invoice;

      // (2026-05-15) Si la factura fue emitida por cuenta de un tercero
      // (doc_tercero / tercero_id), la guia de despacho debe reflejar los
      // datos del TERCERO (RIF y razon social), no del cliente final, y
      // quedar vinculada al indice del tercero. Si no es factura de tercero,
      // se mantiene el comportamiento legacy (cliente final).
      const esTercero = !!(inv.tercero_id && inv.tercero);
      // (2026-05-16 Req#1) Preferir el objeto agrupado (cliente / tercero)
      // y caer al campo plano si no viene. Null-safe.
      const rifMostrar = esTercero
        ? (inv.tercero?.rif || '')
        : (inv.cliente?.rif || invoice.cliente?.rif || inv.cliente_rif || invoice.cliente_rif || '');
      const nombreMostrar = esTercero
        ? (inv.tercero?.nombre_empresa || inv.tercero?.denominacion_comercial || '')
        : (inv.cliente?.nombre || invoice.cliente?.nombre || inv.cliente_nombre || invoice.cliente_nombre || '');

      setDispatchGuide((prev) => ({
        ...prev,
        factura_id: invoice.id,
        factura_cliente_final_rif: rifMostrar,
        factura_cliente_final_nombre: nombreMostrar,
        factura_numero_factura: inv.numero_documento || invoice.numero_documento || inv.numero_factura || invoice.numero_factura,
        factura_numero_control: inv.numero_control || invoice.numero_control,
        destino: inv.cliente?.direccion || invoice.cliente?.direccion || inv.cliente_direccion || invoice.cliente_direccion || '',
        tercero_id: esTercero ? inv.tercero_id : null,
      }));

      setSearchControl("");
      setFilterResults([]);
    } catch {
      toast.error("Error al cargar la factura");
    }
  };

  // Validación de campos requeridos
  const validate = () => {
    const newErrors = {};
    var errorToast = [];
    if (!dispatchGuide.fecha_salida){
      newErrors.fecha_salida = "La fecha de salida es obligatoria";
      errorToast.push("- La fecha de salida es obligatoria");
    }
    if (!dispatchGuide.factura_cliente_final_rif){
      newErrors.factura_cliente_final_rif = "R.I.F. del cliente es obligatorio";
      errorToast.push("- R.I.F. del cliente es obligatorio");
    }
    if (!dispatchGuide.factura_cliente_final_nombre){
      newErrors.factura_cliente_final_nombre = "Nombre del cliente es obligatorio";
      errorToast.push("- Nombre del cliente es obligatorio");
    }
    if (!dispatchGuide.factura_numero_factura){
      newErrors.factura_numero_factura = "Número de factura es obligatorio";
      errorToast.push("- Número de factura es obligatorio");
    }
    if (!dispatchGuide.factura_numero_control){
      newErrors.factura_numero_control = "Número de control es obligatorio";
      errorToast.push("- Número de control es obligatorio");
    }
    if (!dispatchGuide.origen){
      newErrors.origen = "Ubicación de origen es obligatoria";
      errorToast.push("- Ubicación de origen es obligatoria");
    }
    if (!dispatchGuide.destino){
      newErrors.destino = "Dirección de destino es obligatoria";
      errorToast.push("- Dirección de destino es obligatoria");
    }
    // (2026-07-14 Fase C) Motivo del traslado (Catálogo 13). Espejo EXACTO de
    // las reglas del backend, pero SIN fijar 'X1'/'X3' aquí: qué exige cada
    // motivo lo dicen sus FLAGS en el catálogo (requiere_descripcion_servicio /
    // requiere_descripcion). Si SENIAT agrega un motivo con las mismas
    // exigencias, este formulario lo respeta sin tocar código.
    if (!dispatchGuide.motivo_traslado_codigo){
      newErrors.motivo_traslado_codigo = "Mótivo del traslado (Catálogo 13) es obligatorio";
      errorToast.push("- Mótivo del traslado (Catálogo 13) es obligatorio");
    }
    if (motivoSel?.requiere_descripcion_servicio && !dispatchGuide.descripcion_servicio){
      const msg = `La descripción del servicio es obligatoria para el motivo ${motivoSel.codigo} (${motivoSel.descripcion})`;
      newErrors.descripcion_servicio = msg;
      errorToast.push(`- ${msg}`);
    }
    if (dispatchGuide.descripcion_servicio && dispatchGuide.descripcion_servicio.length > 255){
      newErrors.descripcion_servicio = "La descripción del servicio no puede exceder 255 caracteres (tabla 28 SENIAT)";
      errorToast.push("- La descripción del servicio no puede exceder 255 caracteres (tabla 28 SENIAT)");
    }
    if (motivoSel?.requiere_descripcion){
      if (!dispatchGuide.motivo_traslado){
        const msg = `La especificación del motivo es obligatoria para el motivo ${motivoSel.codigo} (${motivoSel.descripcion})`;
        newErrors.motivo_traslado = msg;
        errorToast.push(`- ${msg}`);
      } else if (dispatchGuide.motivo_traslado.length > 55){
        // Tabla 28: motivoTraslado tope 55. El backend también lo rechaza (400).
        newErrors.motivo_traslado = "La especificación del motivo no puede exceder 55 caracteres (tabla 28 SENIAT)";
        errorToast.push("- La especificación del motivo no puede exceder 55 caracteres (tabla 28 SENIAT)");
      }
    }
    // (2026-07-14 Fase C) Los Catálogos 14/15/16 ya no se validan contra listas
    // fijadas aquí: el select solo ofrece filas del catálogo (BD) y se envía su
    // `id`. Se comprueba únicamente que el id elegido siga existiendo en la
    // lista cargada (espejo de la validación del backend, que resuelve por id).
    [
      { campo: "tipo_producto_id", lista: "tipo_producto", label: "Tipo de producto" },
      { campo: "origen_producto_id", lista: "origen_producto", label: "Origen del producto" },
      { campo: "destino_producto_id", lista: "destino_producto", label: "Destino del producto" },
    ].forEach(({ campo, lista, label }) => {
      const val = dispatchGuide[campo];
      if (val === "" || val === null || val === undefined) return;
      if (!catalogosGuia[lista].some((o) => o.id === Number(val))) {
        newErrors[campo] = `${label} inválido: seleccione una opción del catálogo`;
        errorToast.push(`- ${label} inválido: seleccione una opción del catálogo`);
      }
    });
    if (dispatchGuide.placa && dispatchGuide.placa.length > 12){
      newErrors.placa = "La placa no puede exceder 12 caracteres";
      errorToast.push("- La placa no puede exceder 12 caracteres");
    }
    setErrors(newErrors);
    if (errorToast.length > 0){
      toast.error(<div>
        {errorToast.map(item => (
            <span className="text-start">{item}<br/></span>
          ))}
      </div>)
      setButtonDisabled(false);
    }
    return Object.keys(newErrors).length === 0;
  };

  // **********************************************************
  // SUBMIT CON VALIDACIONES
  // **********************************************************
  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonDisabled(true);
    if (!validate()) return;

    // (2026-07-03) SENIAT Fase 4: el payload incluye TODOS los campos
    // nuevos del tipo 04, pero OMITE los que estén vacíos (regla
    // transversal: no emitir etiquetas vacías). Los campos legacy se
    // envían igual que antes.
    const payload = { ...dispatchGuide };
    CAMPOS_SENIAT_GUIA.forEach((campo) => {
      if (payload[campo] === "" || payload[campo] === null || payload[campo] === undefined) {
        delete payload[campo];
      }
    });

    console.log("DispatchGuide enviado al backend:");
    console.log(JSON.stringify(payload, null, 2));

    try {
      if (!dispatchGuide.id) {
        await createDispatchGuide(payload);
        toast.success("Guía de despacho creada correctamente", {
          onClose: () => navigate("/dispatch-guide"),
        });
      }
    } catch (err) {
      console.error("Error del servidor:", err);

      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Error al guardar";

      toast.error(backendMessage);
    } finally {
      setButtonDisabled(false);
    }
  };

  if (loading) return <div>Cargando...</div>;

  // **********************************************************
  // RENDER
  // **********************************************************
  return (
    <div className="mx-auto w-full">
      <ToastContainer />
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
        <div className="rounded-t bg-white mb-0 px-6 py-6 flex justify-between">
          <h6 className="text-blueGray-700 text-xl font-bold">
            {!dispatchGuide.id ? "Crear" : "Actualizar"} Guía de despacho
          </h6>
        </div>

        <div className="flex-auto px-4 lg:px-10 py-10 pt-0 bg-white">
          <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <h4 className="font-bold mb-2">Datos del Emisor</h4>
            <hr className="my-6 border-b-1 border-blueGray-300" />

            <div className="flex flex-wrap">
              <div className="w-full lg:w-3/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">RIF</label>
                <input 
                  disabled
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-gray-200 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.sujeto_retenido_rif || ""} />
              </div>
              <div className="w-full lg:w-5/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Razón Social</label>
                <input 
                  disabled 
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-gray-200 rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.sujeto_retenido_nombre || ""} />
              </div>
              <div className="w-full lg:w-4/12 px-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Fecha salida</label>
                <input
                  type="date"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.fecha_salida || ""}
                  max={today}
                  onChange={(e) => handleFechaEntregaChange(e.target.value.toUpperCase())}
                />
                {errors.fecha_salida && <p className="text-red-500 text-xs mt-1">{errors.fecha_salida}</p>}
              </div>
            </div>

            <hr className="my-6 border-b-1 border-blueGray-300" />
            <h4 className="font-bold mb-2">Documentos Afectados</h4>

            <div className="flex flex-wrap mt-6">
              <div className="w-full lg:w-6/12 px-4">
                <Autocomplete
                  options={filterResults || []}
                  getOptionLabel={(option) =>
                    `Factura: ${option.numero_documento || option.numero_factura} - Nro. Control: ${option.numero_control}`
                  }
                  value={null}
                  onChange={(e, selectedInvoice) => handleInvoiceSelect(selectedInvoice)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar factura por Nro. Control"
                      value={searchControl}
                      onChange={(e) => handleSearchChange(e.target.value.toUpperCase())}
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </div>
              <div className="w-full lg:w-6/12 px-4"></div>
              <div className="w-full lg:w-3/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">R.I.F. (Cliente)</label>
                <input disabled
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.factura_cliente_final_rif || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, factura_cliente_final_rif: e.target.value.toUpperCase() }))
                  }
                />
                {errors.factura_cliente_final_rif && <p className="text-red-500 text-xs mt-1">{errors.factura_cliente_final_rif}</p>}
              </div>
              <div className="w-full lg:w-3/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Razón social (Cliente)</label>
                <input disabled
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.factura_cliente_final_nombre || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, factura_cliente_final_nombre: e.target.value.toUpperCase() }))
                  }
                />
                {errors.factura_cliente_final_nombre && <p className="text-red-500 text-xs mt-1">{errors.factura_cliente_final_nombre}</p>}
              </div>
              <div className="w-full lg:w-3/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Número de factura</label>
                <input disabled
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.factura_numero_factura || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, factura_numero_factura: e.target.value.toUpperCase() }))
                  }
                />
                {errors.factura_numero_factura && <p className="text-red-500 text-xs mt-1">{errors.factura_numero_factura}</p>}
              </div>
              <div className="w-full lg:w-3/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Número de control</label>
                <input disabled
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.factura_numero_control || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, factura_numero_control: e.target.value.toUpperCase() }))
                  }
                />
                {errors.factura_numero_control && <p className="text-red-500 text-xs mt-1">{errors.factura_numero_control}</p>}
              </div>
              <div className="w-full lg:w-6/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Origen</label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.origen || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, origen: e.target.value.toUpperCase() }))
                  }
                />
                {errors.origen && <p className="text-red-500 text-xs mt-1">{errors.origen}</p>}
              </div>
              <div className="w-full lg:w-6/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Destino</label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.destino || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, destino: e.target.value.toUpperCase() }))
                  }
                />
                {errors.destino && <p className="text-red-500 text-xs mt-1">{errors.destino}</p>}
              </div>
              {/* (2026-07-14 Fase C) El motivo del traslado se arma DESDE LA BD
                  (Catálogo 13). Qué exige cada motivo lo dicen sus FLAGS
                  (requiere_descripcion / requiere_descripcion_servicio), no una
                  lista fijada aquí. El texto libre es la ESPECIFICACIÓN del
                  motivo, obligatoria cuando el motivo lo exige. */}
              <div className="w-full lg:w-4/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Motivo de traslado (Catálogo 13)</label>
                <select
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.motivo_traslado_codigo || ""}
                  onChange={(e) => {
                    const codigo = e.target.value;
                    const sel = catalogosGuia.motivo_traslado.find((m) => m.codigo === codigo);
                    setDispatchGuide((prev) => ({
                      ...prev,
                      motivo_traslado_codigo: codigo,
                      // Se limpian los campos que el motivo elegido NO exige,
                      // para no enviar datos huérfanos en el payload.
                      descripcion_servicio: sel?.requiere_descripcion_servicio ? prev.descripcion_servicio : "",
                      motivo_traslado: sel?.requiere_descripcion ? prev.motivo_traslado : "",
                    }));
                  }}
                >
                  <option value="">-- Seleccione motivo --</option>
                  {catalogosGuia.motivo_traslado.map((m) => (
                    <option key={m.id} value={m.codigo}>{m.codigo} — {m.descripcion}</option>
                  ))}
                </select>
                {errors.motivo_traslado_codigo && <p className="text-red-500 text-xs mt-1">{errors.motivo_traslado_codigo}</p>}
              </div>
              <div className="w-full lg:w-8/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">
                  Especificación del motivo {motivoSel?.requiere_descripcion ? "" : "(Opcional)"}
                </label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.motivo_traslado || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, motivo_traslado: e.target.value.toUpperCase() }))
                  }
                />
                {errors.motivo_traslado && <p className="text-red-500 text-xs mt-1">{errors.motivo_traslado}</p>}
              </div>
              {motivoSel?.requiere_descripcion_servicio && (
                <div className="w-full px-4 mt-4">
                  <label className="block text-blueGray-600 text-xs font-bold mb-2">
                    Descripción del servicio (Obligatoria para motivo {motivoSel.codigo})
                  </label>
                  <textarea
                    rows="2"
                    className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    value={dispatchGuide.descripcion_servicio || ""}
                    onChange={(e) =>
                      setDispatchGuide((prev) => ({ ...prev, descripcion_servicio: e.target.value.toUpperCase() }))
                    }
                  />
                  {errors.descripcion_servicio && <p className="text-red-500 text-xs mt-1">{errors.descripcion_servicio}</p>}
                </div>
              )}
              <div className="w-full lg:w-4/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Transportista (Opcional)</label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.transportista || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, transportista: e.target.value.toUpperCase() }))
                  }
                />
              </div>
              <div className="w-full lg:w-2/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Chofer (Opcional)</label>
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.chofer || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, chofer: e.target.value.toUpperCase() }))
                  }
                />
              </div>
              <div className="w-full lg:w-2/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Placa (Opcional)</label>
                {/* (2026-07-03) SENIAT Fase 4: numeroPlaca admite máximo
                    12 caracteres (tabla 28) — se limita con maxLength. */}
                <input
                  type="text"
                  maxLength={12}
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.placa || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, placa: e.target.value.toUpperCase() }))
                  }
                />
                {errors.placa && <p className="text-red-500 text-xs mt-1">{errors.placa}</p>}
              </div>
            </div>

            {/* ****************************************************************
                (2026-07-03) SENIAT Fase 4 (Guía tipo 04): secciones nuevas.
                Todos los campos son OPCIONALES (vacío = no se envía), salvo
                las reglas condicionales del motivo validadas en validate().
                **************************************************************** */}
            <hr className="my-6 border-b-1 border-blueGray-300" />
            <h4 className="font-bold mb-2">Datos SENIAT del Producto (Opcional)</h4>

            <div className="flex flex-wrap">
              {/* (2026-07-14 Fase C) Los 3 selects se arman DESDE LA BD y envían
                  el `id` (estos catálogos no tienen código normativo). */}
              {[
                { campo: "tipo_producto_id", lista: "tipo_producto", label: "Tipo de producto (Catálogo 14)" },
                { campo: "origen_producto_id", lista: "origen_producto", label: "Origen del producto (Catálogo 15)" },
                { campo: "destino_producto_id", lista: "destino_producto", label: "Destino del producto (Catálogo 16)" },
              ].map(({ campo, lista, label }) => (
                <div key={campo} className="w-full lg:w-4/12 px-4 mt-4">
                  <label className="block text-blueGray-600 text-xs font-bold mb-2">{label}</label>
                  <select
                    className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    value={dispatchGuide[campo] ?? ""}
                    onChange={(e) =>
                      setDispatchGuide((prev) => ({
                        ...prev,
                        [campo]: e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                  >
                    <option value="">-- N/A --</option>
                    {catalogosGuia[lista].map((o) => (
                      <option key={o.id} value={o.id}>{o.descripcion}</option>
                    ))}
                  </select>
                  {errors[campo] && <p className="text-red-500 text-xs mt-1">{errors[campo]}</p>}
                </div>
              ))}
            </div>

            <hr className="my-6 border-b-1 border-blueGray-300" />
            <h4 className="font-bold mb-2">Conductor y Vehículo (SENIAT — Opcional)</h4>

            <div className="flex flex-wrap">
              <div className="w-full lg:w-4/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Identificación del conductor</label>
                <input
                  type="text"
                  maxLength={20}
                  placeholder="V12345678"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.conductor_identificacion || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, conductor_identificacion: e.target.value.toUpperCase() }))
                  }
                />
              </div>
              <div className="w-full lg:w-4/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Tipo de licencia</label>
                {/* (2026-07-08) SENIAT Fase 4: "Tipo de licencia" pasa de texto
                    libre a desplegable (Catálogo config /api/config/tipos-licencia).
                    Se guarda la DESCRIPCIÓN (lo que el backend emite como
                    tipoLicencia, máx. 55 chars). Sin .toUpperCase(). */}
                <select
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.conductor_tipo_licencia || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, conductor_tipo_licencia: e.target.value }))
                  }
                >
                  <option value="">— Seleccione —</option>
                  {tiposLicencia.map((t) => (
                    <option key={t.codigo} value={t.descripcion}>
                      {t.descripcion}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full lg:w-4/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Contacto del conductor</label>
                <input
                  type="text"
                  maxLength={255}
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.conductor_contacto || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, conductor_contacto: e.target.value.toUpperCase() }))
                  }
                />
              </div>
              <div className="w-full lg:w-6/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Tipo de vehículo</label>
                <input
                  type="text"
                  maxLength={255}
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.vehiculo_tipo || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, vehiculo_tipo: e.target.value.toUpperCase() }))
                  }
                />
              </div>
              <div className="w-full lg:w-6/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Número de transporte (vehículo)</label>
                <input
                  type="text"
                  maxLength={55}
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.vehiculo_numero || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, vehiculo_numero: e.target.value.toUpperCase() }))
                  }
                />
              </div>
            </div>

            <hr className="my-6 border-b-1 border-blueGray-300" />
            <h4 className="font-bold mb-2">Transportista — Datos SENIAT (Opcional)</h4>

            <div className="flex flex-wrap">
              <div className="w-full lg:w-4/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">R.I.F. del transportista</label>
                <input
                  type="text"
                  maxLength={20}
                  placeholder="J-12345678-9"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.transportista_rif || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, transportista_rif: e.target.value.toUpperCase() }))
                  }
                />
              </div>
              <div className="w-full lg:w-8/12 px-4 mt-4">
                <label className="block text-blueGray-600 text-xs font-bold mb-2">Domicilio fiscal del transportista</label>
                <input
                  type="text"
                  maxLength={255}
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  value={dispatchGuide.transportista_domicilio || ""}
                  onChange={(e) =>
                    setDispatchGuide((prev) => ({ ...prev, transportista_domicilio: e.target.value.toUpperCase() }))
                  }
                />
              </div>
            </div>

            <div className={FORM_FOOTER_CONTAINER}>
              <button
                type="button"
                className={FORM_BTN_CANCEL}
                disabled={buttonDisabled}
                onClick={() => navigate("/dispatch-guide")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={FORM_BTN_SUBMIT}
                disabled={buttonDisabled}
              >
                {buttonDisabled ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default FormDispatchGuides;
